import React from 'react'
import { CCard, CCardBody, CPlaceholder } from '@coreui/react-pro'

/** 12480 -> "12,480". Nulls render as an em dash rather than "0" or "NaN". */
export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString()
}

export const formatDecimal = (value, places = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toFixed(places)
}

/**
 * Every number on this dashboard carries a comparison — a bare count tells an
 * admin nothing. Returns the direction, the percentage and a colour.
 *
 * Growth from zero has no meaningful percentage (it is not "infinity% up"), so
 * it is reported as "new" rather than a fabricated number.
 */
export const computeDelta = (current, previous) => {
  const cur = Number(current) || 0
  const prev = Number(previous) || 0

  if (prev === 0 && cur === 0) {
    return { direction: 'flat', label: 'No change', color: 'medium-emphasis', pct: 0 }
  }
  if (prev === 0) {
    return { direction: 'up', label: 'New', color: 'success', pct: null }
  }

  const pct = ((cur - prev) / prev) * 100
  const rounded = Math.abs(pct) < 0.05 ? 0 : pct

  if (rounded === 0) {
    return { direction: 'flat', label: 'No change', color: 'medium-emphasis', pct: 0 }
  }

  return {
    direction: rounded > 0 ? 'up' : 'down',
    label: `${rounded > 0 ? '▲' : '▼'} ${Math.abs(rounded).toFixed(1)}%`,
    color: rounded > 0 ? 'success' : 'danger',
    pct: rounded,
  }
}

/** "26 Aug" / "26 Aug 2026" — avoids ambiguous 08/09 ordering for AU vs US admins. */
export const formatDateLabel = (isoDate, withYear = false) => {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  })
}

export const formatRangeLabel = (from, to) => {
  if (!from || !to) return ''
  return `${formatDateLabel(from)} – ${formatDateLabel(to, true)}`
}

/**
 * Per-tile skeletons rather than one full-page spinner, so the layout does not
 * jump and the admin can see what is arriving.
 */
export const TileSkeleton = ({ height = 110 }) => (
  <CCard className="mb-4 av2-skeleton" style={{ minHeight: height }}>
    <CCardBody>
      <CPlaceholder component="div" animation="glow">
        <CPlaceholder xs={5} className="mb-2" />
        <CPlaceholder xs={8} size="lg" className="mb-2" />
        <CPlaceholder xs={4} />
      </CPlaceholder>
    </CCardBody>
  </CCard>
)

export const ChartSkeleton = ({ height = 300 }) => (
  <div className="av2-skeleton d-flex align-items-end gap-2 px-2" style={{ height }}>
    <CPlaceholder component="div" animation="glow" className="w-100 d-flex align-items-end gap-2">
      {[55, 80, 40, 95, 65, 75, 50].map((h, i) => (
        <CPlaceholder key={i} style={{ height: `${h}%`, flex: 1, borderRadius: '4px 4px 0 0' }} />
      ))}
    </CPlaceholder>
  </div>
)

/**
 * A real empty state. Never an empty axis, never a spinner that never stops.
 */
export const EmptyState = ({
  title = 'No app activity in this period',
  hint = 'Try widening the date range, or clearing the platform and version filters.',
  height = 300,
}) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center text-center px-3"
    style={{ minHeight: height }}
  >
    <div className="fs-5 fw-semibold mb-1">{title}</div>
    <div className="text-body-secondary small" style={{ maxWidth: 420 }}>
      {hint}
    </div>
  </div>
)

/** Surfaced when a request fails, so a failure never looks like "zero traffic". */
export const ErrorState = ({ message, onRetry, height = 300 }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center text-center px-3"
    style={{ minHeight: height }}
  >
    <div className="fs-6 fw-semibold text-danger mb-1">Could not load this section</div>
    <div className="text-body-secondary small mb-3" style={{ maxWidth: 420 }}>
      {message || 'The analytics service did not respond.'}
    </div>
    {onRetry && (
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
        Try again
      </button>
    )}
  </div>
)

/**
 * Charts stay readable at ~10 categories. Anything past the cap is summed into
 * a single "Other" row rather than dropped, so the total still adds up.
 */
export const capWithOther = (rows, limit, valueKeys, labelKey) => {
  if (!Array.isArray(rows) || rows.length <= limit) return rows || []

  const head = rows.slice(0, limit)
  const tail = rows.slice(limit)

  const other = { [labelKey]: `Other (${tail.length})`, __isOther: true }
  valueKeys.forEach((key) => {
    other[key] = tail.reduce((sum, row) => sum + (Number(row[key]) || 0), 0)
  })

  return [...head, other]
}
